import './Visualization.scss'
import * as Cesium from 'cesium'
import { useEffect, useMemo, useRef, useState } from 'react'
import GroundStationCard from '../../../../CommonComponents/GroundStationCard'
import GroundTargetCard from '../../../../CommonComponents/GroundTargetCard'
import MultiGroundObjectCard from '../../../../CommonComponents/MultiGroundObjectCard'
import useViewer from '../../hooks/useViewer'
import useStaticData from '../../hooks/useStaticData'
import { getEntityDetails } from '../../../../../utils/cesiumUtil'

const isCesiumDestroyedError = (error: unknown) => {
    const cesiumError = error as { name?: string; message?: string }
    return cesiumError?.name === 'DeveloperError' && cesiumError?.message?.includes('destroyed')
}

const Visualization = () => {

    const { viewer } = useViewer()
    const { allGeoFeatures, allStations, allTaskPlans } = useStaticData()

    const previousZoom = useRef<number | null>(null);

    const [selectedGs, setSelectedGs] = useState<any>(null)
    const [selectedTask, setSelectedTask] = useState<any>(null)
    const [selectedMultiObject, setSelectedMultiObject] = useState<any>(null)

    const structerdTaskPlan = useMemo(() => Object.entries(allTaskPlans).map(([_, taskplans]) => taskplans).flat(), [allTaskPlans])

    useEffect(() => {
        if (!viewer) return
        const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
        handler.setInputAction((movement: { endPosition: Cesium.Cartesian2 }) => {
            if (!viewer || viewer.isDestroyed() || !viewer.scene) return;
            if ((window as any).predictionCleanupInProgress) {
                document.body.style.cursor = "auto";
                return;
            }
            try {
                const pickedObjects = viewer.scene.drillPick(movement.endPosition);
                if (pickedObjects && pickedObjects.length > 0) {
                    let stationEntity: any[] = [];
                    let stationPositions: { latitude: number, longitude: number }[] = [];
                    let targetEntity: any[] = [];
                    let targetPositions: { latitude: number, longitude: number }[] = [];

                    pickedObjects.forEach((pickedObject: any) => {
                        if (Cesium.defined(pickedObject) && Cesium.defined(pickedObject.id)) {
                            const entity = pickedObject.id;
                            const description =
                                (entity && entity.description) &&
                                entity.description.getValue(Cesium.JulianDate.now());
                            const details = getEntityDetails(description, entity?._id, allStations, structerdTaskPlan, allGeoFeatures)
                            stationEntity = [...stationEntity, ...details.stationEntity]
                            stationPositions = [...stationPositions, ...details.stationPositions]
                            targetEntity = [...targetEntity, ...details.targetEntity]
                            targetPositions = [...targetPositions, ...details.targetPositions]
                        }
                    });

                    // Check how many entities are picked
                    const hasMultipleStations = stationEntity.length > 1;
                    const hasMultipleTargets = targetEntity.length > 1;
                    const hasBothOne = stationEntity.length === 1 && targetEntity.length === 1;
                    if (hasMultipleStations || hasMultipleTargets || hasBothOne) {
                        // Collect all positions
                        const allPositions = [...stationPositions, ...targetPositions];
                        const minLat = Math.min(...allPositions.map(p => p.latitude)) || 0;
                        const maxLon = Math.max(...allPositions.map(p => p.longitude)) || 0;
                        setSelectedMultiObject({
                            stations: stationEntity,
                            targets: targetEntity,
                            latitude: minLat,
                            longitude: maxLon,
                        });
                        setSelectedGs(null);
                        setSelectedTask(null);
                    } else if (!hasBothOne && stationEntity.length === 1) {
                        setSelectedGs(stationEntity[0]);
                        setSelectedTask(null);
                        setSelectedMultiObject(null);
                    } else if (!hasBothOne && targetEntity.length === 1) {
                        setSelectedTask(targetEntity[0]);
                        setSelectedGs(null);
                        setSelectedMultiObject(null);
                    } else {
                        setSelectedTask(null);
                        setSelectedGs(null);
                        setSelectedMultiObject(null);
                    }
                    document.body.style.cursor = "pointer";
                } else {
                    // No entity under cursor → clear selections
                    setSelectedTask(null);
                    setSelectedGs(null);
                    setSelectedMultiObject(null);
                    document.body.style.cursor = "auto";
                }
            } catch (error) {
                if (isCesiumDestroyedError(error)) {
                    setSelectedTask(null);
                    setSelectedGs(null);
                    setSelectedMultiObject(null);
                    document.body.style.cursor = "auto";
                    return;
                }
                console.log(error);
            }
        }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

        return () => {
            handler.destroy();
        };
    }, [viewer, allStations, structerdTaskPlan, allGeoFeatures])

    useEffect(() => {
        if (!viewer) return;
        const handleCameraChange = () => {
            const cameraPosition = viewer.camera.positionCartographic;
            const zoom = cameraPosition.height; // Height is a proxy for zoom
            if (previousZoom.current === null) {
                previousZoom.current = zoom;
            }
            if (previousZoom.current !== zoom) {
                previousZoom.current = zoom;
                setSelectedGs(null)
                setSelectedTask(null)
                setSelectedMultiObject(null)
            }
        };
        viewer.camera.changed.addEventListener(handleCameraChange);
        return () => {
            viewer.camera.changed.removeEventListener(handleCameraChange);
        };
    }, [viewer]);

    return (
        <div className='scenario__visualization__container'>
            <div id="scenariocesiumContainer" className="d-flex h-100 w-100"></div>
            <GroundStationCard station={selectedGs} viewer={viewer} />
            <GroundTargetCard target={selectedTask} viewer={viewer} />
            <MultiGroundObjectCard assets={selectedMultiObject} viewer={viewer} />
        </div>
    )
}

export default Visualization
